import React, {useState} from 'react';
import {Add, ReplayOutlined} from '@material-ui/icons';
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {useForm} from 'react-hook-form';

import {colors} from '../../../../assets/styles/colors';
import ArrowLeftIcon from '../../../../elements/icons/ArrowLeftIcon';
import XmarkIcon from '../../../../elements/icons/XmarkIcon';
import {classNames} from '../../../../utils/react';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useFetchProducts, useProducts, useProductPagination} from '../../utils';
import {
  useSelectedProductActions,
  useSelectedProducts,
  useSelectedProductsVariants,
} from '../../utils/bulkEditOptions';
import {
  extractInfoProduct,
  updateDataProductsNewStock,
} from '../../utils/extractInfoProduct';
import {useAlert} from '../../../../elements/Alert/AlertContext';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {ProductBulkEdit} from '../../../../entities';
import {customErrorBulkMessage} from '../../utils/customErrorMessage';

const useStyles = makeStyles(theme => ({
  infoText: {
    color: colors.grey700,
    fontSize: '.85rem',
    maxWidth: '100%',

    [theme.breakpoints.up('md')]: {
      maxWidth: '40ch',
    },

    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  scrollArrow: {
    padding: '.75rem',
  },
  icon: {
    width: '2.2rem',
    height: '2.2rem',
  },
  productQuantitySelected: {
    fontWeight: 600,
    width: '230px',
  },
  productQuantitySelectedSpan: {
    color: colors.grey700,
    fontSize: '.85rem',
  },
  flexCenterColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '0.1rem',
  },
  iconButtonBack: {
    background: 'white',
  },
  iconButton: {
    background: 'white',

    '&:hover': {
      background: colors.purple1,
    },
  },
  iconButtonActive: {
    background: colors.purple1,
    color: colors.purple3,
    fontWeight: 600,
  },
  iconButtonMobile: {
    background: 'white',
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  textFieldCustom: {
    width: '10rem',

    '& input': {
      textAlign: 'right',
    },

    [theme.breakpoints.down('md')]: {
      minWidth: '8rem',
      width: '100%',
    },
  },
  ofertGroupButtonInput: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
    rowGap: '1rem',
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      width: 'fit-content',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  ofertGroupButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
  },
  contentBack: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      width: 'fit-content',
    },
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'space-between',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  button: {
    width: '300px',
    backgroundColor: colors.success,
    color: '#fff',
    fontSize: '1rem',

    '&:hover': {
      backgroundColor: '#01815f',
    },

    [theme.breakpoints.down('sm')]: {
      width: '50%',
      margin: '0 auto',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  containerOption: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    overflowX: 'auto',
    gap: '5%',

    [theme.breakpoints.down('md')]: {
      overflowX: 'hidden',
      flexDirection: 'column',
      rowGap: '2rem',
    },

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  spaceBetwen: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  groupButtonApply: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'fit-content',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  iconButtonDesktopOption: {
    display: 'none',
    background: 'white',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
}));

type showOptionBulk = 'NONE' | 'ALL' | 'CATEGORY' | 'OFERT' | 'STOCK' | 'PRICE';

type Props = {
  productCount: number,
  variantCount: number,
  setShowBulkOption: (value: showOptionBulk) => void,
  removeAllToggleProductSelection: () => void,
};

export default function BulkEditNewStock({
  productCount,
  variantCount,
  setShowBulkOption,
  removeAllToggleProductSelection,
}: Props) {
  const classes = useStyles();
  const {register, handleSubmit, errors, reset} = useForm();
  const {getProducts} = useFetchProducts();

  const [optionSelected, setOptionSelected] = useState('ADD');

  const {isFetchingProducts} = useProducts();
  const pagination = useProductPagination();
  const productSelection = useSelectedProducts();
  const productsVariantsSelection = useSelectedProductsVariants();
  const {editSelectedProducts} = useSelectedProductActions();
  const {showAlert} = useAlert();

  const editDataProducts = (data: ProductBulkEdit[]) => {
    editSelectedProducts(data)
      .then(() => {
        showAlert('Productos actualizados', {type: 'success'});
        getProducts({
          page: pagination.page,
          size: pagination.size,
        });
      })
      .catch(e => {
        const errorMessage = customErrorBulkMessage(e.error);
        showAlert(errorMessage, {type: 'error'});
      });
  };

  const updateProductsNewStock = data => {
    const {productsInfo} = extractInfoProduct(
      productSelection,
      productsVariantsSelection
    );

    const updatedProducts = updateDataProductsNewStock(
      productsInfo,
      productSelection,
      Number(data.stock),
      optionSelected
    );

    editDataProducts(updatedProducts);
  };

  return (
    <div className={classes.containerOption}>
      <div className={classes.contentBack}>
        <div className={classes.contentBack}>
          <IconButton
            className={classes.iconButtonBack}
            onClick={() => {
              setShowBulkOption('ALL');
            }}
          >
            <ArrowLeftIcon />
          </IconButton>

          <div className={classes.flexCenterColumn}>
            <Typography className={classes.productQuantitySelected}>
              Stock Nuevo
            </Typography>
            <Typography
              component={'span'}
              className={classes.productQuantitySelectedSpan}
            >
              Producto{productCount > 1 ? 's' : ''} seleccionado
              {productCount > 1 ? 's' : ''}: {productCount}
            </Typography>
            <Typography
              component={'span'}
              className={classes.productQuantitySelectedSpan}
            >
              Variante{variantCount > 1 ? 's' : ''} seleccionado
              {variantCount > 1 ? 's' : ''}: {variantCount}
            </Typography>
          </div>
        </div>
        <IconButton
          className={classes.iconButtonMobile}
          onClick={() => {
            removeAllToggleProductSelection();
            setShowBulkOption('ALL');
          }}
        >
          <XmarkIcon />
        </IconButton>
      </div>
      <form
        className={classes.content}
        onSubmit={handleSubmit(updateProductsNewStock)}
      >
        <div className={classes.row}>
          <div className={classes.ofertGroupButtonInput}>
            <div className={classes.ofertGroupButtons}>
              <Button
                className={classNames(classes.iconButton, {
                  [classes.iconButtonActive]: optionSelected === 'UPDATE',
                })}
                onClick={() => {
                  setOptionSelected('UPDATE');
                  reset();
                }}
              >
                <ReplayOutlined />
              </Button>
              <Button
                className={classNames(classes.iconButton, {
                  [classes.iconButtonActive]: optionSelected === 'ADD',
                })}
                onClick={() => {
                  setOptionSelected('ADD');
                  reset();
                }}
              >
                <Add />
              </Button>
            </div>

            <CustomTextField
              name="stock"
              className={classes.textFieldCustom}
              inputRef={register({
                validate: value => {
                  if (isNaN(value)) {
                    return 'Ingrese un valor numérico';
                  }

                  if (!/^\d+$/.test(value)) {
                    return 'El stock debe ser un número entero';
                  }

                  if (optionSelected === 'UPDATE' && Number(value) < 0) {
                    return 'El stock debe ser mayor o igual a 0';
                  }

                  if (
                    optionSelected === 'ADD' &&
                    (Number(value) < 0 || Number(value) === 0)
                  ) {
                    return 'El stock debe ser mayor a 0';
                  }
                  return true;
                },
              })}
              id="input-with-icon-textfield"
              variant="outlined"
              type="text"
              error={!!errors.stock}
              helperText={errors.stock?.message}
            />
          </div>

          {optionSelected === 'UPDATE' ? (
            <Typography className={classes.infoText}>
              Se actualizará el stock de productos seleccionados.
            </Typography>
          ) : (
            <Typography className={classes.infoText}>
              Se sumará el stock en las unidades indicadas de productos
              seleccionados.
            </Typography>
          )}
        </div>

        <div className={classes.groupButtonApply}>
          {isFetchingProducts ? (
            <CircularProgress />
          ) : (
            <CustomButton type="submit" className={classes.button}>
              Aplicar
            </CustomButton>
          )}
          <IconButton
            className={classes.iconButtonDesktopOption}
            onClick={() => {
              removeAllToggleProductSelection();
              setShowBulkOption('ALL');
            }}
          >
            <XmarkIcon />
          </IconButton>
        </div>
      </form>
    </div>
  );
}
